import { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';

export interface Movimiento {
  id?: string;
  concepto: string;
  responsable: string;
  fecha: string;
  monto: number;
  estado: string;
  referencia?: string; // DEPRECATED: Mantener para compatibilidad. Usar numeroDocumento para nuevos movimientos.
  tipo: 'ingreso' | 'egreso' | 'comision' | 'abono';
  lotes?: string[];
  comprador?: string;
  vendedor?: string;
  origen?: string;
  automatic?: boolean; // true = automático, false/undefined = manual
  parentId?: string; // ID del movimiento padre (para cascada)
  childIds?: string[]; // IDs de movimientos hijos (para cascada)
  createdAt?: string;
  updatedAt?: string;
  // NUEVOS campos para alineación con estructura del banco (opcionales)
  numeroDocumento?: string; // El "No. Doc." del banco - usado para match con estados de cuenta bancarios
  tipoTransaccion?: string; // El "TT" del banco (opcional)
  transferencia?: string; // Número de transferencia (puede ser el mismo que numeroDocumento o diferente)
}

export function useMovimientos() {
  const [movimientos, setMovimientos] = useState<Movimiento[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMovimientos = useCallback(async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'movimientos'));
      const movimientosData: Movimiento[] = [];
      querySnapshot.forEach((doc) => {
        movimientosData.push({ id: doc.id, ...doc.data() } as Movimiento);
      });
      setMovimientos(movimientosData);
      setError(null);
    } catch (err) {
      console.error('Error fetching movimientos:', err);
      setError('Error al cargar movimientos');
    } finally {
      setLoading(false);
    }
  }, []);

  const addMovimiento = useCallback(async (movimiento: Omit<Movimiento, 'id'>, customId?: string) => {
    try {
      const movimientoData = {
        ...movimiento,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      if (customId) {
        await setDoc(doc(db, 'movimientos', customId), movimientoData);
        return customId;
      } else {
        const docRef = await addDoc(collection(db, 'movimientos'), movimientoData);
        return docRef.id;
      }
    } catch (err) {
      console.error('Error adding movimiento:', err);
      setError('Error al agregar movimiento');
      throw err;
    }
  }, []);

  const updateMovimiento = useCallback(async (id: string, updates: Partial<Movimiento>) => {
    try {
      const updateData = {
        ...updates,
        updatedAt: new Date().toISOString()
      };
      await updateDoc(doc(db, 'movimientos', id), updateData);
    } catch (err) {
      console.error('Error updating movimiento:', err);
      setError('Error al actualizar movimiento');
      throw err;
    }
  }, []);

  const deleteMovimiento = useCallback(async (id: string) => {
    try {
      await deleteDoc(doc(db, 'movimientos', id));
    } catch (err) {
      console.error('Error deleting movimiento:', err);
      setError('Error al eliminar movimiento');
      throw err;
    }
  }, []);

  // Suscripción en tiempo real a los movimientos
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'movimientos'), (snapshot) => {
      const movimientosData: Movimiento[] = [];
      snapshot.forEach((doc) => {
        movimientosData.push({ id: doc.id, ...doc.data() } as Movimiento);
      });
      setMovimientos(movimientosData);
      setLoading(false);
    }, (err) => {
      console.error('Error listening to movimientos:', err);
      setError('Error al sincronizar movimientos');
    });

    return () => unsubscribe();
  }, []);

  return {
    movimientos,
    loading,
    error,
    fetchMovimientos,
    addMovimiento,
    updateMovimiento,
    deleteMovimiento
  };
} 